/**
 * ImpactGauge — 4-cell ink ladder gauge for concours impact.
 *
 * Eames spec (E5):
 *   none → 0 filled cells, label "None — original spec"
 *   minor → 1 filled cell, label "Light"
 *   moderate → 2 filled cells, label "Notable"
 *   major → 3 filled cells, label "Significant"
 *
 * Design rules:
 *   - No hue, no pink — ink/border only.
 *   - Filled cell: background T.text (ink), empty: transparent (hairline border retained).
 *   - Label: mono uppercase; T.textMuted for "none" (resting/good-news), T.text for others.
 *   - Bar is aria-hidden (decorative-redundant); aria-label on wrapper carries meaning.
 *   - Meaning never relies on color alone (text label + fill count both carry it).
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import type { ConcoursImpact } from '@/types/domain';
import type { CSSProperties } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_CELLS = 4;

const FILL_COUNT: Record<ConcoursImpact, number> = {
  none: 0,
  minor: 1,
  moderate: 2,
  major: 3,
};

const LABEL: Record<ConcoursImpact, string> = {
  none: 'None (original spec)',
  minor: 'Light',
  moderate: 'Notable',
  major: 'Significant',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ImpactGaugeProps {
  impact: ConcoursImpact;
  /** Optional extra style on the wrapper element. */
  style?: CSSProperties;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ImpactGauge({ impact, style }: ImpactGaugeProps) {
  const fillCount = FILL_COUNT[impact];
  const label = LABEL[impact];
  const isNone = impact === 'none';

  return (
    <span
      aria-label={`Concours impact: ${label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: SPACE.sm,
        ...style,
      }}
    >
      {/* Bar — decorative; meaning carried by the text label */}
      <span aria-hidden="true" style={{ display: 'flex', gap: SPACE['2xs'] }}>
        {Array.from({ length: TOTAL_CELLS }, (_, i) => {
          const filled = i < fillCount;
          return (
            <span
              key={i}
              style={{
                display: 'inline-block',
                width: 10,
                height: 6,
                borderRadius: RADIUS.sm,
                border: `1px solid ${T.border}`,
                background: filled ? T.text : 'transparent',
                // Optional transition when a cell flips after an edit.
                transition: 'background var(--motion-short, 150ms ease)',
              }}
            />
          );
        })}
      </span>

      {/* Label */}
      <span
        style={{
          fontFamily: F.mono,
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: isNone ? T.textMuted : T.text,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </span>
  );
}
